import {
    Mesh,
    DoubleSide,
    MeshBasicMaterial
} from 'three';

import { VRObject3D } from './VRObject3D';
import { squareGeometry, emptyTexture, INDICATOR_HIT_SIZE } from './helpers/texturesAndGeometries';
import { initQuillEditor, setQuillContent, getQuillContent, getQuillEditor } from './helpers/editorHelpers';
import { AMPLITUDE_EVENTS, trackAnalytics } from '../Tracking/baglessAnalytics';


class VRInfoElement extends VRObject3D {

    constructor(containerName, camera, texture, x, y, z, model) {

        super(containerName);

        this.camera = camera;
        this.material = new MeshBasicMaterial({ map: texture, side: DoubleSide, transparent: true });

        this.mesh = new Mesh(squareGeometry, this.material);
        this.mesh.type = 'infoElement';
        this.mesh.name = 'infoElement';
        this.name = 'infoElement';
        this.type = 'infoElement';

        this.model = model;
        this.defaultTexture = texture;
        // Use Icon on: 2D info indicator. Use Icon off: mesh shows the uploaded image.
        this.useIcon = true;
        this.customIcon = false;

        // INIT
        this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);

        this.add(this.mesh);

        this.mesh.groupDetails = { title: $trans('info-element'), icon: 'info', editTitle: $trans('edit-info-element'), dataLang: 'edit-info-element' }

        this.editControls();

        //result
        return this;
    }

    setDbConfig(config) {
        super.setDbConfig(config);

        if (config.useIcon !== undefined) {
            this.useIcon = config.useIcon === true;
        } else {
            this.useIcon = !this.getCustomImageUrl();
        }
        this.customIcon = this.useIcon === false;
        this.dbConfig.useIcon = this.useIcon;
        this.updateMeshTexture();
    }

    isDefaultMarkerImage = (url) => {
        return !url || String(url).includes('/empty.png') || String(url).endsWith('empty.png');
    }

    getCustomImageUrl = () => {
        const url = this.dbConfig?.image?.url;
        return this.isDefaultMarkerImage(url) ? null : url;
    }

    isIndicatorMode = () => {
        return !!this.useIcon || !this.customIcon;
    }

    isClickableInView() {
        if (this.model?.editMode) return false;
        if (this.isIndicatorMode()) return false;
        if (!this.mesh || this.mesh.visible === false) return false;
        if (this.userData?.hiddenBySceneAction) return false;
        if (this.dbConfig?.hideOnStart) return false;
        const mat = this.mesh.material;
        if (mat && !Array.isArray(mat) && typeof mat.opacity === 'number' && mat.opacity <= 0.01) {
            return false;
        }
        return true;
    }

    applyIndicatorHitGeometry = () => {
        this.updateMeshGeometry(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
        if (this.mesh.menuPosition) {
            this.mesh.menuPosition.position.y = 0;
            this.mesh.menuPosition.translateY((INDICATOR_HIT_SIZE / 2 + 15) * -1);
        }
    }

    setUseIcon = (useIcon) => {
        const next = !!useIcon;
        if (this.useIcon === next) {
            this.customIcon = !next;
            return;
        }

        this.useIcon = next;
        this.customIcon = !next;
        if (this.dbConfig) this.dbConfig.useIcon = this.useIcon;
        this.updateMeshTexture();
    }

    updateMeshTexture = (imageLoadCallback = null) => {
        if (this.useIcon) {
            if (this.model?.editMode) {
                this.mesh.material.map = emptyTexture;
                this.applyIndicatorHitGeometry();
            } else {
                this.mesh.material.map = this.defaultTexture;
            }
            this.mesh.material.needsUpdate = true;
            imageLoadCallback && imageLoadCallback();
            return;
        }

        const url = this.getCustomImageUrl();
        const aspectRatio = this.dbConfig?.image?.aspectRatio || 1;
        if (url) {
            this.setGeometry(url, aspectRatio, true);
            imageLoadCallback && imageLoadCallback();
            return;
        }

        if (this.model?.editMode) {
            this.mesh.material.map = emptyTexture;
            this.applyIndicatorHitGeometry();
        } else {
            this.mesh.material.map = this.defaultTexture;
        }
        this.mesh.material.needsUpdate = true;
        imageLoadCallback && imageLoadCallback();
    }

    showEditModal = () => {
        $(".info-element-modal .modal-header-title").text($trans('edit-info-element'));
        $(".info-element-modal .modal-header-title").attr('data-lang', 'edit-info-element');
        $(".info-element-modal").addClass("edit");

        const { data } = this.dbConfig;
        const { title, description, button, image } = data;
        const markerImage = this.dbConfig.image;
        const hasMarker = !this.isDefaultMarkerImage(markerImage?.url);
        const useIcon = this.dbConfig.useIcon !== undefined ? this.dbConfig.useIcon === true : !hasMarker;

        $("#use_info_element_icon").prop('checked', useIcon);
        $("#info_element_marker_image").attr('src', hasMarker ? markerImage.url : 'textures/empty.png');
        $("#info_element_marker_image_aspect_ratio").val(markerImage?.aspectRatio || 1);
        $("#upload_info_element_marker_image .upload-watermark").toggleClass('hide', hasMarker);
        $("#removeInfoElementMarkerImage").toggleClass('active', hasMarker);

        const isLocation = button.hasOwnProperty('isLocation') ? button.isLocation : false;

        $("#info-element-title").val(title);

        // Initialize Quill editor if not already initialized
        if (!this.quillEditor) {
            const selector = "#info-element-description1";
            this.quillEditor = getQuillEditor(selector) || initQuillEditor(selector, {
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline'], ['link'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['clean']
                    ]
                },
                placeholder: 'Enter description...'
            });
        }

        // Set content
        setQuillContent("#info-element-description1", description);

        const showAdvertisementLinks = this.dbConfig?.advertisementLinks === '1';
        $("#info-element-description-advertisement-links").prop("checked", showAdvertisementLinks);

        $("#info-element-button-text").val(button.text);
        $("#info-element-button-url").val(button.url);

        $("#locationButton").val(isLocation ? button.locationID : $("#locationButton option:first").val());
        $(".locationButton .select-selected").text(isLocation ? button.locationName : '');

        $("#info_element_image").attr("src", data.hasOwnProperty('image') ? data.image || './textures/empty.png' : './textures/empty.png');
        $("#info_element_video").attr("src", data.hasOwnProperty('video') ? data.video || '#' : '#');

        const imageCond = !$("#info_element_image").attr("src").includes('/empty.png');
        const videoCond = $("#info_element_video").attr("src") !== '#';

        $("#upload_info_element_image .upload-watermark").toggleClass("hide", imageCond);
        $("#upload_info_element_video .upload-watermark").toggleClass("hide", videoCond);
        $("#info_element_video").parent().toggleClass("active", videoCond);
        $("#info_element_video").toggleClass("active", videoCond);

        $("#removeInfoElementImage").toggleClass("active", imageCond);
        $("#removeInfoElementVideo").toggleClass("active", videoCond);

        $("#info-element-modal").addClass("is-active");
    }

    openModal = () => {
        const { data } = this.dbConfig;
        const { title, description, button, image } = data;
        $(".info-element-modal").removeClass("edit");
        $("#infoElementTitle").text(title);
        const imgDom = image ? `<div class='infoModalImage'><img src='${image}''></div>` : '';
        const descDom = description ? `<div class='infoModalDescription'>${description}</div>` : '';
        let videoDom = '';
        if (data.hasOwnProperty('video')) {
            videoDom = data.video ? `<div id="info_element_video_holder" class="active"><video id="infoElementVideo" autoplay muted><source src='${data.video}'></source></video><div id="unmute_info_element_video"><span class='material-icons md-18'>volume_off</span></div></div>` : '';
        }
        const showAdvertisementLinks = this.dbConfig?.advertisementLinks === '1';
        if (!showAdvertisementLinks) {
            $(".info-element-description-advertisement-links").parent().remove();
        }

        let wrap = '';
        wrap += imgDom;
        wrap += videoDom;
        wrap += descDom;

        const isLocation = button.hasOwnProperty('isLocation') ? button.isLocation : false;
        $("#infoElementBtnUrl").toggleClass("isLocation", isLocation);

        $("#infoElementBtnUrl").attr("href", isLocation ? button.locationID : button.url);
        $("#infoElementBtnText").text(button.text);

        $("#infoElementModal footer").toggleClass("visible", button.show);
        $("#info-element-content").html(wrap);

        const anyDescription = descDom !== "";

        $("#info-element-content").toggleClass('pad-top', anyDescription && !imgDom);
        $("#info-element-content").toggleClass('pad-bot', anyDescription);
        $("#info-element-content").toggleClass('pad-bot-big', anyDescription && !button.show);
        $("#infoElementModal footer").toggleClass('no-mar', button.show && !anyDescription);

        $("#infoElementModal").addClass("is-active");
        trackAnalytics(this.model, AMPLITUDE_EVENTS.SAW_INFO_PANEL, {
            object_id: this.dbConfig?.id,
            object_type: 'infoElement',
            object_label: 'Info element',
            object_name: this.dbConfig?.data?.title,
        });
    }

    closeModal = () => {
        const $modal = $("#infoElementModal");
        if (!$modal.hasClass("is-active")) return;
        $modal.removeClass("is-active");
        const videoDom = $("#infoElementVideo");
        if (videoDom.length > 0) {
            const video = videoDom.get(0);
            video.pause();
            video.currentTime = 0;
        }
    }
}

export { VRInfoElement };
